import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSourceAdmin, updateSource } from "@/lib/admin-sources.functions";
import { sourceInputSchema } from "@/lib/admin-sources.schemas";
import { SourceForm, type SourceFormValues } from "@/components/admin/SourceForm";

export const Route = createFileRoute("/_authenticated/_admin/admin/sources/$id/edit")({
  component: EditSourcePage,
});

function EditSourcePage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const load = useServerFn(getSourceAdmin);
  const submit = useServerFn(updateSource);
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-sources", "detail", id],
    queryFn: () => load({ data: { id } }),
  });

  if (isLoading) {
    return <p style={{ padding: "2rem", color: "var(--text-muted)" }}>Lade Quelle…</p>;
  }
  if (error || !data) {
    return (
      <p style={{ padding: "2rem", color: "var(--text-primary)" }}>
        Die Quelle konnte nicht geladen werden. Bitte lade die Seite neu.
      </p>
    );
  }

  const defaults: Partial<SourceFormValues> = {
    key: data.key ?? "",
    title: data.title ?? "",
    publisher: data.publisher ?? "",
    year: data.year ?? "",
    kind: data.kind as SourceFormValues["kind"],
    url: data.url ?? "",
    backs_de: data.backs_de ?? "",
    backs_en: data.backs_en ?? "",
    caveat_de: data.caveat_de ?? "",
    caveat_en: data.caveat_en ?? "",
    published: data.published ?? true,
    sort_order: data.sort_order ?? 0,
  };

  return (
    <SourceForm
      title="Quelle bearbeiten"
      mode="edit"
      submitting={submitting}
      defaultValues={defaults}
      onSubmit={async (values) => {
        const parsed = sourceInputSchema.safeParse({
          ...values,
          caveat_de: values.caveat_de.trim() || null,
          caveat_en: values.caveat_en.trim() || null,
        });
        if (!parsed.success) {
          toast.error("Eingabe ungültig", { description: parsed.error.issues.map((i) => i.message).join("; ") });
          return;
        }
        setSubmitting(true);
        try {
          await submit({ data: { id, data: parsed.data } });
          toast.success("Quelle aktualisiert");
          qc.invalidateQueries({ queryKey: ["admin-sources"] });
          navigate({ to: "/admin/sources" });
        } catch (e) {
          toast.error("Speichern fehlgeschlagen", { description: e instanceof Error ? e.message : String(e) });
        } finally {
          setSubmitting(false);
        }
      }}
    />
  );
}
